import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'

import { useOnClickOutside } from '@/hooks/useOnClickOutside'

import type { Meridiem, MeridiemParts } from '../_utils/scheduleFormat'
import { formatScheduleTime, fromMeridiemParts, toMeridiemParts } from '../_utils/scheduleFormat'
import S from './ScheduleTimePicker.style'

const MERIDIEMS: Meridiem[] = ['오전', '오후']
const HOURS = Array.from({ length: 12 }, (_, i) => i + 1)
/** 반나절을 시간순으로 훑는 순서. 12시가 그 반나절의 시작(00:00 / 12:00)이다 */
const HOURS_CHRONOLOGICAL = [12, ...HOURS.slice(0, 11)]
const MINUTES = [0, 30]

type Props = {
  value: Date
  onChange: (slot: Date) => void
  isSlotSelectable: (slot: Date) => boolean
  disabled?: boolean
}

const ScheduleTimePicker = ({ value, onChange, isSlotSelectable, disabled }: Props) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  useOnClickOutside(ref, () => setOpen(false), open)

  // 열릴 때 각 칼럼의 선택 항목이 가운데 오도록 스크롤한다. 시 칼럼은 12개라 안 보이는 위치에 있을 수 있다
  useEffect(() => {
    if (!open || !popoverRef.current) return
    popoverRef.current.querySelectorAll<HTMLElement>('.column').forEach((column) => {
      const active = column.querySelector<HTMLElement>('.item.active')
      if (!active) return
      column.scrollTop = active.offsetTop - column.clientHeight / 2 + active.offsetHeight / 2
    })
  }, [open])

  const parts = toMeridiemParts(value)
  const candidate = (patch: Partial<MeridiemParts>) =>
    fromMeridiemParts(value, { ...parts, ...patch })

  const halfDaySlots = (meridiem: Meridiem) =>
    HOURS_CHRONOLOGICAL.flatMap((hour12) =>
      MINUTES.map((minute) => candidate({ meridiem, hour12, minute })),
    )
  const firstSelectableInHalfDay = (meridiem: Meridiem) =>
    halfDaySlots(meridiem).find(isSlotSelectable)

  // 오전/오후를 바꿀 때 현재 시·분이 막혀 있으면 그 반나절에서 시간순 첫 활성 슬롯으로 옮긴다
  const pickMeridiem = (meridiem: Meridiem) => {
    const direct = candidate({ meridiem })
    const next = isSlotSelectable(direct) ? direct : firstSelectableInHalfDay(meridiem)
    if (next) onChange(next)
  }

  const renderItem = (
    key: string,
    label: string,
    selectable: boolean,
    active: boolean,
    onPick: () => void,
  ) => {
    return (
      <button
        key={key}
        type="button"
        role="option"
        aria-selected={active}
        className={clsx('item', active && 'active', !selectable && 'disabled')}
        disabled={!selectable}
        onClick={onPick}
      >
        {label}
      </button>
    )
  }

  return (
    <div css={S.wrap} ref={ref}>
      <button
        type="button"
        css={S.field}
        aria-label="예약 시간"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
      >
        {formatScheduleTime(value)}
      </button>
      {open && (
        <div css={S.popover} role="listbox" aria-label="예약 시간 선택" ref={popoverRef}>
          <div className="column">
            {MERIDIEMS.map((meridiem) =>
              renderItem(
                meridiem,
                meridiem,
                // 그 반나절에 고를 수 있는 슬롯이 하나라도 있으면 열어 둔다
                !!firstSelectableInHalfDay(meridiem),
                parts.meridiem === meridiem,
                () => pickMeridiem(meridiem),
              ),
            )}
          </div>
          <div className="column">
            {HOURS.map((hour12) =>
              renderItem(
                `h${hour12}`,
                `${hour12}`,
                isSlotSelectable(candidate({ hour12 })),
                parts.hour12 === hour12,
                () => onChange(candidate({ hour12 })),
              ),
            )}
          </div>
          <div className="column">
            {MINUTES.map((minute) =>
              renderItem(
                `m${minute}`,
                minute === 0 ? '00' : `${minute}`,
                isSlotSelectable(candidate({ minute })),
                parts.minute === minute,
                () => onChange(candidate({ minute })),
              ),
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default ScheduleTimePicker
